import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { AppBar, Box, IconButton, Skeleton, Stack, Tooltip, Toolbar, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { GlobalSearch } from '@/components/common/GlobalSearch'
import { NotificationsBell } from '@/features/notifications/NotificationsBell'
import { useActiveTerritory } from '@/hooks/useActiveTerritory'
import { useSystemHealth } from '@/hooks/useSystemHealth'
import { UserMenu } from '@/layouts/components/UserMenu'
import { TOPBAR_HEIGHT } from '@/theme/layout'
import { navItems } from '@/routes/navigation'

function usePageTitle(): string {
  const { pathname } = useLocation()
  const match = navItems.find((item) => (item.path === '/' ? pathname === '/' : pathname.startsWith(item.path)))
  return match?.label ?? 'GOVIA'
}

/**
 * Franja de contexto — nunca expone el estado crudo del backend
 * ("Backend operativo"/"development"/"production"): traduce siempre a
 * lenguaje de producto ("Monitoreo activo"/"Monitoreo interrumpido").
 * Se oculta en pantallas pequeñas (vista ejecutiva resumida) para no
 * competir con el título de la página.
 */
function ContextRow() {
  const { territory, isLoading: territoryLoading } = useActiveTerritory()
  const healthQuery = useSystemHealth()
  const monitoringActive = healthQuery.data?.status === 'ok'

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      divider={<Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />}
      sx={{ display: { xs: 'none', md: 'flex' } }}
    >
      {territoryLoading ? (
        <Skeleton variant="text" width={100} />
      ) : territory ? (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <PlaceRoundedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary">
            {territory.label}
          </Typography>
        </Stack>
      ) : null}
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <FiberManualRecordRoundedIcon
          sx={{ fontSize: 8, color: monitoringActive ? 'success.main' : 'text.disabled' }}
        />
        <Typography variant="caption" color="text.secondary">
          {monitoringActive ? 'Monitoreo activo' : 'Monitoreo interrumpido'}
        </Typography>
      </Stack>
    </Stack>
  )
}

export function Topbar({ onMenuClick }: { onMenuClick: () => void }) {
  const title = usePageTitle()
  const [searchOpen, setSearchOpen] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  return (
    <AppBar position="sticky" elevation={0} color="transparent">
      <Toolbar sx={{ gap: 1.5, minHeight: `${TOPBAR_HEIGHT}px !important`, py: 1 }}>
        <IconButton
          edge="start"
          onClick={onMenuClick}
          sx={{ display: { xs: 'inline-flex', md: 'none' } }}
          aria-label="Abrir navegación"
        >
          <MenuRoundedIcon />
        </IconButton>

        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {title}
          </Typography>
          <ContextRow />
        </Stack>

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Buscar (Ctrl+K)">
          <IconButton onClick={() => setSearchOpen(true)} aria-label="Buscar">
            <SearchRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <NotificationsBell />
        <UserMenu />
      </Toolbar>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </AppBar>
  )
}
